import React, { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import {
  getCurrentUser,
  AuthUser,
  signOut,
  signInWithRedirect,
  fetchAuthSession,
} from 'aws-amplify/auth';

import { environmentService } from '../services/environmentService';
import { isLocalAuth } from '../services/authToken';
import { ADMIN_CLEARANCE_LEVEL } from '../services/types';
import { createAmplifyConfig } from '../config/amplify';

interface AuthContextType {
  user: AuthUser | null;
  clearanceLevel: number | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [clearanceLevel, setClearanceLevel] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Configure Amplify and check auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get environment config
        const config = await environmentService.getConfig();

        // Local development (scripts/dev.sh): no Cognito, a fixed local user, an admin.
        // API requests carry a local token instead (services/authToken.ts).
        if (isLocalAuth(config)) {
          setUser({ username: 'local-dev', userId: 'local-dev' });
          setClearanceLevel(ADMIN_CLEARANCE_LEVEL);
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // Configure Amplify with loaded environment config
        Amplify.configure(createAmplifyConfig(config));

        // Check auth state after Amplify is configured
        await checkAuthState();
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);

      // Pull Entra ClearanceLevel from the Cognito idToken custom claim.
      // Cognito serialises custom attributes as strings; coerce to number.
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload;
      const raw = claims?.['custom:clearanceLevel'];
      let level: number | null = null;
      if (typeof raw === 'number') {
        level = raw;
      } else if (typeof raw === 'string' && raw.trim() !== '') {
        const parsed = Number(raw);
        level = Number.isFinite(parsed) ? parsed : null;
      }
      setClearanceLevel(level);
    } catch (error) {
      setUser(null);
      setClearanceLevel(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      // Sign in using Microsoft Entra SAML provider specifically
      await signInWithRedirect({
        provider: { custom: 'MicrosoftEntra' },
      });
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
      setClearanceLevel(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value: AuthContextType = {
    user,
    clearanceLevel,
    isLoading,
    isAuthenticated,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
